import { parseISO } from 'date-fns'
import React, { FC, useEffect, useState } from 'react'

import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { useParams } from 'react-router'

import Accordion from '../../accordion/common/Accordion'
import SwitchElement from '../../elements/toggles/toggles/SwitchElement'
import ViewsLoader from '../../loader/ViewsLoader'
import { DaysTabs } from '../common/DaysTabs/DaysTabs'
import ExhibitorOrderOfGoAccordion from './details/accordions/ExhibitorOrderOfGoAccordion'

import { getFloatPrice } from '../../../helpers/price'
import { daysOfWeek, extractDatePart, sortScheduling } from '../../../helpers/time'
import { getAllRegistrationFees } from '../../../helpers/registrationFees'
import { getConvertedData } from '../../../models/interface.helper'
import { getCurrency } from '../../../helpers/payment'
import { normalizeOrder } from '../../../helpers/helpers'

import useHorse from '../../../pages/organizer/manage/common/table/HorsesManage/hooks/useHorse'

import FirestoreService from '../../../services/firestoreService'
import RegistrationDyDayService from '../../../services/firebase-service/registration-by-day/RegistrationDyDayService'

import { EventFeesModel } from '../../../models/event-fees/event-fees.model'
import { IBreak } from '../../../models/events/event.interface'
import { EventModel } from '../../../models/events/event.model'
import { IRegistrationByDayInterface } from '../../../models/registrations-by-day/registrationByDay.interface'
import { IClassOption, IClassOptionRegistered } from './details/EventOrderOfGo'

import { CONST } from '../../../const/const'
import { EventReviewPublishModel } from '../../../models/event-review-publish/event-review-publish.model'
import { IEventReviewPublish } from '../../../models/event-review-publish/event-review-publish.interface'
import { RegistrationFeesType } from '../../../models/event-fees/event-fees.interface'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface ScheduleByClassProps {
  activeDay: string
  setActiveDay: (value: string) => void
  days: string[]
  setDays: (value: string[]) => void
  hideEntries: boolean
}

export const ScheduleByClass: FC<ScheduleByClassProps> = ({
  activeDay,
  setActiveDay,
  days,
  setDays,
  hideEntries,
}) => {
  const { eventId } = useParams<{ eventId: string }>()
  const { horses } = useHorse(eventId)

  const [hideEmptyClasses, setHideEmptyClasses] = React.useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [breaks, setBreaks] = useState<IBreak[]>([])
  const [filteredClasses, setFilteredClasses] = useState<IClassOptionRegistered[] | null>(null)
  const [classes, setClasses] = useState<IClassOptionRegistered[] | null>(null)
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  useEffect(() => {
    let filteredClasses = classes
    if (hideEmptyClasses) {
      filteredClasses = classes?.filter((item) => item.registered && item.registered.length) || []
    }
    setFilteredClasses(filteredClasses)
  }, [classes, hideEmptyClasses])

  const handleGetFeesData = async () => {
    setLoading(true)

    try {
      let riders = await RegistrationDyDayService.getByEventIds([eventId])
      riders = riders.filter((rider) => !rider.orderOfGoScratched)

      const eventSnapShot = await FirestoreService.getItem(COLLECTIONS.EVENTS.NAME, eventId)
      const eventData = getConvertedData(EventModel.fromFirestoreDoc(eventSnapShot).toObject())

      const eventFeesSnap = await FirestoreService.getItem(COLLECTIONS.EVENT_FEES.NAME, eventId)
      const eventFees = EventFeesModel.fromFirestoreDoc(eventFeesSnap).toObject()

      const eventReviewPublishSnap = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )

      const flattenedFees = getAllRegistrationFees(eventFees?.registrationFees || [])

      const publishedEvent_ =
        EventReviewPublishModel.fromFirestoreDoc(eventReviewPublishSnap).toObject()
      setPublishedEvent(publishedEvent_)

      const days_: string[] = []

      const classOption: IClassOption[] = Object.values(
        sortScheduling(flattenedFees).reduce((acc: any, item) => {
          const extractedDate = extractDatePart(item.startDate)
          const date = parseISO(extractedDate)
          const day = daysOfWeek[date.getDay()]

          if (!days_.includes(day)) days_.push(day)

          acc[item.uuid] = {
            value: item.uuid,
            label: item.name,
            actualCost: (item as RegistrationFeesType)?.actualCost,
            dayOfWeek: day,
            fullTime: `${item.startTimeHours}:${item.startTimeMinutes} ${item.startTimeFormat}`,
            increment: (item as RegistrationFeesType)?.increment,
            location: item.location,
            order: (item as RegistrationFeesType)?.order || 0,
          }

          return acc
        }, {})
      )

      setDays(days_)
      setActiveDay(days_[0])

      const combinedArray = classOption?.map((item1) => {
        const correspondingItem = riders.filter((item2) => item1.value === item2.uuid)

        let count = 0
        const uniqueData = correspondingItem.reduce((acc: IRegistrationByDayInterface[], rider) => {
          // Check if the riderId of the current item is already in the accumulator
          const isRiderIdExist = acc.some(
            (item) => item.riderId === rider.riderId && item.horseId === rider.horseId
          )

          // If riderId doesn't exist, add the current item to the accumulator
          if (!isRiderIdExist) {
            const newItem = {
              ...rider,
            }

            acc.push({
              ...newItem,
              order:
                rider.orderOfGoScratched || rider.order === 0
                  ? // || rider.isScratched
                    0
                  : rider.order || count + 1,
            })
            if (!rider.orderOfGoScratched && rider.order !== 0) count++
          }
          return acc
        }, [])

        return {
          ...item1,
          registered: uniqueData,
        }
      })
      setClasses(combinedArray as IClassOptionRegistered[])

      setBreaks(eventData?.breaks || [])
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    handleGetFeesData().then()
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[100px]">
        <ViewsLoader
          className="flex items-center w-full justify-center min-h-[30vh] mt-3"
          size="lg"
          color="#F70763"
        />
      </div>
    )
  }

  return filteredClasses && filteredClasses.length > 0 ? (
    <>
      <div className={'flex w-full justify-between items-center gap-3 flex-wrap'}>
        <DaysTabs activeDay={activeDay} setActiveDay={setActiveDay} days={days} />
        {!hideEntries && (
          <div className={'flex gap-2 items-center mb-6'}>
            <span className={'text-sm'}>Hide empty classes</span>
            <SwitchElement
              buttonClassName="small"
              on={hideEmptyClasses}
              onSwitch={() => setHideEmptyClasses((val) => !val)}
            />
          </div>
        )}
      </div>
      {filteredClasses
        ?.sort((a, b) => normalizeOrder(a.order) - normalizeOrder(b.order))
        .map((item, index) => {
          const classBreaks = breaks.filter((b) => b.registrationByDayUuid === item.value)
          const EventSchedule = publishedEvent?.EventSchedule

          let registered = 0

          if (item.registered && Array.isArray(item.registered)) registered = item.registered.length

          const isPublishOrder =
            EventSchedule?.classGroupPublish?.[item.value]?.publishOrder || false

          if (item.dayOfWeek === activeDay) {
            return (
              <Accordion
                key={index}
                disabled={hideEntries}
                icon={<AddCircleOutline className="add_icon text-[red]" />}
                initialState={false}
                status={isPublishOrder ? 'Available' : 'Pending'}
                className="mb-2 w-full"
                header={
                  <div className="flex flex-col xl:flex-row gap-2 text-[14px] justify-between pr-[10px]">
                    <div>
                      {item.label}
                      {item.order ? (
                        <span className="ml-2 text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                          {item.order}
                        </span>
                      ) : null}
                    </div>

                    <div className={'text-[#122B4680] font-normal'}>
                      <span className={'customValue text-[#122B46]'}>
                        {getFloatPrice(`${currency}${item.actualCost}`)}
                      </span>{' '}
                      entry fee • <span className={'customValue text-[#122B46]'}>{registered}</span>{' '}
                      registered •{' '}
                      <span className={'customValue text-[#122B46]'}>{item.fullTime}</span>,{' '}
                      {item.dayOfWeek} • <span className="text-[#122B46]">{item.location}</span>
                    </div>
                  </div>
                }
                headerTextClassName="text-SeabiscuitDark200ThemeColor capitalize w-full pr-2"
                activeIcon={<RemoveCircleOutline className="add_icon text-[red]" />}
              >
                <ExhibitorOrderOfGoAccordion
                  increment={item.increment}
                  data={[...item.registered, ...classBreaks]}
                  isPublishOrder={isPublishOrder}
                  horses={horses}
                  eventId={eventId}
                  flattenedFees={getAllRegistrationFees(
                    publishedEvent?.EventFees?.registrationFees || []
                  )}
                  isRiderMode={false}
                />
              </Accordion>
            )
          } else {
            return null
          }
        })}
    </>
  ) : (
    <p className="text-SeabiscuitDark200ThemeColor/50">Classes not found</p>
  )
}
